import useCartStore from '~/shared/store/modules/cart';
import useMainStore from '~/shared/store';
import useAccountStore from "~/shared/store/modules/account";
import useCatalogStore from "~/shared/store/modules/catalog";
import useCompaniesStore from "~/shared/store/modules/account/companies";
import getUserProfile from "../get-user-profile";
import customFetch from "~/composables/custom-fetch";
import endpoints from '~/shared/config/api';
import type {ICatalogCategory} from "~/shared/interfaces/catalog";
import type {FetchError} from "ofetch";
import type {IError, IResponse} from "~/shared/interfaces/api";
import type { ICartPreview, ICartProduct } from '~/shared/interfaces/cart';
import type { IPartnerPositionOnAdd } from '~/shared/interfaces/search/products';
import type { IPositionOnAdd } from '~/shared/interfaces/downloadPositions';
import type {IVinCatalogs} from "~/shared/interfaces/vin";

async function getInitialData() {
  const mainStore = useMainStore();
  const cartStore = useCartStore();
  const accountStore = useAccountStore();
  const catalogStore = useCatalogStore();
  const companiesStore = useCompaniesStore()
  const { cart} = endpoints;

  async function getCartPreview() {
    let cartPreviewData: ICartPreview[] | null = null;

    if (accountStore.userRole != 'guest') {
      const { data: cartPreview, error } = await useFetch('/api/cart/getCartPreview');

      if (error.value?.status == 401) {
        await navigateTo('/');
        return;
      }

      cartPreviewData = cartPreview.value;
    }

    if (process.client) {
      const cartPreviewFromLs = localStorage.getItem('cartPreview');

      if (cartPreviewFromLs) {
        cartPreviewData = JSON.parse(cartPreviewFromLs) as ICartPreview[];
      }
    }

    if (cartPreviewData) {
      cartStore.resetPreviewList(cartPreviewData);
    }
  }

  async function getVinCatalogs() {
    const { data: vinCatalogs, error } = await useFetch<{data: IVinCatalogs[]}>('/api/vin/getCatalogs');

    if(vinCatalogs.value?.data) {
      mainStore.setVinCatalogs(vinCatalogs.value.data)
    }
  }

  async function getSotransCities() {
    const { data: cities } = await useFetch('/api/cities/getSotransCities');

    if (cities.value) {
      mainStore.setSotransCities(cities.value)
    }
  }

  async function getInitialCategoriesList() {
    const { data } = await useFetch<ICatalogCategory[], FetchError<IError>>('/api/catalog/getInitialCategories');

    if (data.value) {
      catalogStore.setCatalogCategories(data.value);
    }
  }

  async function getUserBalance() {
    await getUserProfile()
    if (accountStore.userRole !== 'guest') {
      const { data: balance, error } = await useFetch('/api/account/getAccountBalance');

      if (error.value?.status == 401) {
        await navigateTo('/');
        return;
      }
      accountStore.setBalance(balance.value ?? []);
    }
  }

  await Promise.allSettled([
    getUserBalance(),
    getSotransCities(),
    getVinCatalogs(),
    getInitialCategoriesList()
  ])

  if (process.client) {
    const isGuest = accountStore.userRole == 'guest';
    const products = JSON.parse(localStorage.getItem('cartProducts') || '[]') as ICartProduct[];
    const requestsList = [] as Promise<IResponse<null>>[];
    localStorage.setItem('user-role', isGuest ? 'guest' : 'auth-user');
    if (products && !isGuest) {
      const { partner, oneCProducts } = products.reduce<{
        partner: IPartnerPositionOnAdd[],
        oneCProducts: IPositionOnAdd[],
      }>((acc, { id, type, city, count, price, article, title, warehouse, brand }) => {
        const productId = id.split('_')[0];

        if (type == 'partner') {
          acc.partner.push({
            productId,
            count,
            citySlug: city.slug,
          })
        }else {
          acc.oneCProducts.push({
            productId,
            price,
            article,
            title,
            warehouseId: warehouse.id as number,
            count,
            brandId: brand.id,
          })
        }

        return acc;
      }, {
        partner: [],
        oneCProducts: [],
      })

      if (partner.length) {
        requestsList.push(customFetch(cart.addToCartPartnerProduct(partner)));
      }

      if (oneCProducts.length) {
        requestsList.push(customFetch(cart.addToCart1CProduct(oneCProducts)));
      }

      await Promise.allSettled(requestsList)

      localStorage.removeItem('cartProducts');
      localStorage.removeItem('cartPreview');
    }

    await getCartPreview();
  }
}

export default getInitialData;
